import { Transition } from '@headlessui/react'
import type { FormikProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import sanitize from 'sanitize-html'
import * as Yup from 'yup'

import Save from '../../../assets/images/icons/save-02.svg?react'
import Cancel from '../../../assets/images/icons/x-close-table.svg?react'
import { useMePreferencesQuery } from '../../../store/api'
import { useAddUrlMutation } from '../../../store/api/product'
import type { CustomBaseQueryError } from '../../../store/types/error'
import PrimaryButton from '../../../utils/buttons/primary-button'
import { useFastFetchModal } from '../../../utils/context/modal/use-fast-fetch-modal'
import { IntercomEvent } from '../../../utils/helpers/getIntercomMsg'
import useIntercomInternal from '../../../utils/hooks/use-intercom-internal'

type AddUrlFormProps = {
  isAddingUrl: boolean
  setIsAddingUrl: Dispatch<SetStateAction<boolean>>
  productId: number
  warning?: string
  setWarning: Dispatch<SetStateAction<string | undefined>>
  hasIsMineUrl: boolean
}

const urlValidationSchema = Yup.object({
  url: Yup.string()
    .matches(
      /^(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,})(\/[^\s]+)$/,
      'The URL format must be either <u>http(s)://www.domain.com/path</u> or <u>http(s)://domain.com/path</u>.',
    )
    .required('Please Enter URL'),
})

export default function AddUrlForm({
  isAddingUrl,
  setIsAddingUrl,
  productId,
  warning,
  setWarning,
  hasIsMineUrl,
}: AddUrlFormProps) {
  const {
    setTrackerId,
    setCompetitorName,
    setIsMine,
    doNotShow,
    open: openFastFetchModal,
  } = useFastFetchModal()

  const { data } = useMePreferencesQuery()

  const [addUrlMutator, { isError, error, reset }] = useAddUrlMutation()

  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (isError) {
      const onMouseDown = () => {
        reset()
      }
      document.addEventListener('click', onMouseDown)
      return () => {
        document.removeEventListener('click', onMouseDown)
      }
    }
  }, [isError])

  useEffect(() => {
    if (isError && error) {
      setErrorMessage(sanitize((error as CustomBaseQueryError).data?.message))
    } else {
      setErrorMessage('')
    }
  }, [error, isError])

  useEffect(() => {
    if (warning) {
      const onMouseDown = () => {
        setWarning(undefined)
      }
      document.addEventListener('click', onMouseDown)

      return () => {
        document.removeEventListener('click', onMouseDown)
      }
    }
  }, [warning])

  const { trackEventInternal } = useIntercomInternal()

  if (data?.monitoring_source === 'DATAEDIS' && hasIsMineUrl) {
    return <></>
  }

  return (
    <div className="w-full mb-4 px-6">
      <div className=" inline-flex w-full items-center gap-x-4">
        <PrimaryButton
          onClick={() => setIsAddingUrl(true)}
          disabled={isAddingUrl}
          classNameText="px-3.5 text-primary-700"
          classNameButton="bg-primary-50 h-11"
        >
          {data?.monitoring_source === 'DATAEDIS' ? 'Add Own URL' : 'Add URL'}
        </PrimaryButton>
        {isAddingUrl && (
          <Formik
            initialValues={{
              url: '',
            }}
            validationSchema={urlValidationSchema}
            onSubmit={async (values, { setFieldError }) => {
              if (productId) {
                try {
                  const res = await addUrlMutator({
                    id: productId,
                    url: values.url,
                  }).unwrap()
                  if (res.is_mine) {
                    trackEventInternal(IntercomEvent['ismine-url-added'], {
                      'Product Id': productId,
                      Url: values.url,
                    })
                  } else {
                    trackEventInternal(IntercomEvent['url-added'], {
                      'Product Id': productId,
                      Url: values.url,
                    })
                  }
                  if (res?.warning) {
                    setWarning(res?.warning.message)
                  }
                  setIsAddingUrl(false)
                  setTrackerId(res.tracker_id)
                  setCompetitorName(values.url)
                  setIsMine(res.is_mine)
                  if (!doNotShow) {
                    openFastFetchModal()
                  }
                } catch (err) {
                  trackEventInternal(IntercomEvent['add-url-failed'])
                  setIsAddingUrl(true)
                }
              }
            }}
          >
            {(props: FormikProps<any>) => (
              <Form className="flex-grow">
                <div className="inline-flex gap-x-4 w-full items-center">
                  <p className="text-gray-700 text-sm">URL:</p>
                  <Field name="url">
                    {({ field, form: { touched, errors }, meta }: any) => {
                      if (meta.error) {
                        setErrorMessage(errors?.url)
                      } else if (!isError) {
                        setErrorMessage('')
                      }

                      return (
                        <div className="relative flex flex-grow">
                          <input
                            className="border border-gray-300 rounded-md w-full py-1 px-3 focus:outline-none text-gray-700 text-sm"
                            type="text"
                            {...field}
                          />
                        </div>
                      )
                    }}
                  </Field>

                  <button
                    type="submit"
                    disabled={
                      !props.isValid || !props.dirty || errorMessage !== ''
                    }
                    className="group"
                  >
                    <Save className="group-disabled:[&>path]:stroke-zinc-600 group-disabled:opacity-50" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setErrorMessage('')
                      setIsAddingUrl(false)
                    }}
                  >
                    <Cancel />
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
      <Transition
        enter="transition-opacity duration-75"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
        show={warning !== undefined || errorMessage !== ''}
      >
        <div className="mt-2">
          {errorMessage !== '' ? (
            <p
              className="text-xs text-error-600"
              dangerouslySetInnerHTML={{
                __html: errorMessage,
              }}
            ></p>
          ) : (
            <p
              className="text-warning-300 text-xs"
              dangerouslySetInnerHTML={{ __html: sanitize(warning!) }}
            ></p>
          )}
        </div>
      </Transition>
    </div>
  )
}
